/**
 * Regeneration als Schaetzmodell (F-34 C3) - rein und getestet. Keine
 * Messung und keine medizinische Aussage: Schlaf, Ernaehrung und Stress
 * wirken individuell, das eigene Koerpergefuehl hat Vorrang.
 *
 * Erholungszeit je Belastung = Grundzeit (nach Muskelgroesse) × Umfang × Anstrengung.
 *
 * - Grundzeit: grosse Muskeln 72 h, mittlere 60 h, kleine 48 h. Angelehnt an
 *   die verbreitete Empfehlung von 48-72 h zwischen zwei Einheiten derselben
 *   Muskelgruppe (Schoenfeld & Grgic 2018, Strength Cond J 40:107) und die
 *   bis zu 36 h erhoehte Proteinsynthese nach schwerem Training (MacDougall
 *   et al. 1995, Can J Appl Physiol 20:480); dass grosse Muskelgruppen
 *   laenger brauchen, beschreiben Bishop, Jones & Woods 2008 (J Strength Cond
 *   Res 22:1015) als Faustregel.
 * - Umfang: 0,6 + 0,08 je Satz (5 Saetze = 1,0), begrenzt auf 0,6-1,6.
 * - Anstrengung aus „Wie leicht fiel es?“ (1-10, 10 = leicht): 0,8 + 0,04 × (11 − leicht),
 *   ohne Angabe 1,0.
 *
 * Alle Startwerte sind in den Einstellungen aenderbar.
 */
import { MUSKEL_IDS, MUSKELN, beitragJeMuskel, type MuskelId, type Groesse } from '@/domain/training/muskeln';
import type { TrainingsProtokoll, Uebung } from '@/domain/training/typen';

export interface RegenerationsWerte { gross: number; mittel: number; klein: number }

export const STANDARD_REGENERATION: RegenerationsWerte = { gross: 72, mittel: 60, klein: 48 };

const STUNDE = 3_600_000;

export const umfangsFaktor = (saetze: number) => Math.min(1.6, Math.max(0.6, 0.6 + 0.08 * saetze));
export const anstrengungsFaktor = (leicht?: number) => (leicht ? 0.8 + 0.04 * (11 - Math.min(10, Math.max(1, leicht))) : 1);

/** Zeitpunkt der Belastung: Ende der Einheit, sonst 18 Uhr am Tag. */
function zeitpunkt(p: TrainingsProtokoll): number {
  return p.ende ? Date.parse(p.ende) : Date.parse(`${p.datum}T18:00:00`);
}

export type ErholungsStufe = 'frisch' | 'teilweise' | 'erholt';

export interface Erholung {
  /** 0-100 */
  prozent: number;
  stufe: ErholungsStufe;
  /** Zeitpunkt (ms), ab dem der Muskel laut Schaetzung erholt ist; `null` = schon erholt. */
  erholtAb: number | null;
  /** Letzte Belastung (ms) in den letzten 7 Tagen. */
  letzte: number | null;
  /** Vom eigenen Gefuehl ueberschrieben (Muskelkater/muede). */
  gefuehl: boolean;
}

export const stufeVon = (prozent: number): ErholungsStufe => (prozent < 33 ? 'frisch' : prozent < 90 ? 'teilweise' : 'erholt');

export const ERHOLUNG_TEXT: Record<ErholungsStufe, string> = { frisch: 'frisch belastet', teilweise: 'teilweise erholt', erholt: 'erholt' };

/** Muskelkater oder „fuehlt sich muede an“ zaehlt fuer diesen Tag als frisch belastet. */
export const GEFUEHL_PROZENT = 25;

export function erholung(
  protokolle: TrainingsProtokoll[], bibliothek: ReadonlyMap<string, Uebung>, jetzt: number,
  werte: RegenerationsWerte = STANDARD_REGENERATION, muede: ReadonlySet<MuskelId> = new Set(),
): Record<MuskelId, Erholung> {
  const out = Object.fromEntries(MUSKEL_IDS.map((m) => [m, { prozent: 100, stufe: 'erholt', erholtAb: null, letzte: null, gefuehl: false } as Erholung])) as Record<MuskelId, Erholung>;
  const ab = jetzt - 7 * 24 * STUNDE;
  for (const p of protokolle) {
    if (p.status !== 'fertig') continue;
    const t = zeitpunkt(p);
    if (t < ab || t > jetzt) continue;
    const last = new Map<MuskelId, number>();
    for (const pu of p.uebungen) {
      const u = bibliothek.get(pu.uebungId);
      if (!u || u.typ === 'mobility' || pu.uebersprungen) continue;
      const n = pu.saetze.filter((s) => s.erledigt && s.art !== 'aufwaermen').length;
      for (const [m, w] of beitragJeMuskel(u.hauptmuskeln, u.hilfsmuskeln)) last.set(m, (last.get(m) ?? 0) + w * n);
    }
    for (const [m, saetze] of last) {
      if (saetze <= 0) continue;
      const groesse: Groesse = MUSKELN[m].groesse;
      const dauer = werte[groesse] * umfangsFaktor(saetze) * anstrengungsFaktor(p.leicht) * STUNDE;
      const prozent = Math.min(100, Math.round(((jetzt - t) / dauer) * 100));
      const e = out[m];
      e.letzte = Math.max(e.letzte ?? 0, t);
      if (prozent < e.prozent) e.prozent = prozent;
      if (t + dauer > jetzt) e.erholtAb = Math.max(e.erholtAb ?? 0, t + dauer);
    }
  }
  for (const m of MUSKEL_IDS) {
    const e = out[m];
    if (muede.has(m)) { e.gefuehl = true; e.prozent = Math.min(e.prozent, GEFUEHL_PROZENT); }
    e.stufe = stufeVon(e.prozent);
  }
  return out;
}

/** Fuer die Uebersicht: der am wenigsten erholte Muskel einer Gruppe zaehlt (bei Gleichstand der spaeter erholte). */
export function erholungDerGruppe(e: Record<MuskelId, Erholung>, muskeln: MuskelId[]): Erholung {
  return muskeln.map((m) => e[m]).reduce((a, b) => (b.prozent < a.prozent || (b.prozent === a.prozent && (b.erholtAb ?? 0) > (a.erholtAb ?? 0)) ? b : a));
}

/** Hauptmuskeln geplanter Uebungen, die laut Schaetzung noch nicht erholt sind. */
export function nochNichtErholt(e: Record<MuskelId, Erholung>, hauptmuskeln: Iterable<MuskelId>): MuskelId[] {
  return [...new Set(hauptmuskeln)].filter((m) => e[m].stufe !== 'erholt');
}
